import { useEffect, useMemo, useState } from "react"
import { AnimatePresence, motion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { Product } from "../../appData/productData"
import { formatCurrency } from "../../utils/utils"
import styles from "./DeepFreezePaywall.module.scss"

/**
 * DeepFreezePaywall — an original, self-contained EXPIRATION paywall.
 *
 * Concept ("Your vault is freezing over"): the user's access is lapsing, so the
 * screen literally performs a deep freeze. A live countdown ticks toward zero,
 * a "% frozen" meter climbs, and the user's own memories (real photos) frost
 * over and crack one by one inside a chilled glass vault. The threat is rendered
 * in the brand's cold blue; the rescue is rendered in the brand's warm amber —
 * the single prominent YEARLY card is a pocket of heat that visibly melts the
 * frost around it. Cold = loss, warmth = the annual plan.
 *
 * The whole layout funnels to the YEARLY plan: yearly is the only large, glowing,
 * tappable surface (its annual price is the single biggest number on screen, with
 * a tiny per-week + per-month equivalent so the commitment feels small and it stays
 * App Store-compliant), the savings vs monthly is shouted, and monthly is demoted
 * to one quiet line of text.
 *
 * Fully self-contained: no shared paywall components are reused. It reads real
 * pricing from the app's debug product data and talks to the native bridge
 * directly, so it renders standalone with the built-in debug data.
 */

// Real "memories" (Unsplash) that frost over inside the vault. Sized + cropped
// to faces so each tile reads as a personal photo even while icing up. An
// onError fallback keeps every tile an intentional cold gradient if offline.
const MEMORY_IMG =
  "https://images.unsplash.com/photo-IMGID?ixlib=rb-4.1.0&auto=format&fit=crop&crop=faces,center&w=420&h=420&q=70"

const MEMORIES = [
  { id: "1636408244294-f0b1309f5422", label: "Summer, 2019" },
  { id: "1516151322954-621642a2b64b", label: "Santorini" },
  { id: "1627366285897-f040e8432f0f", label: "Day we got Cooper" },
  { id: "1562804698-732e972e46e4", label: "Mara's 30th" },
  { id: "1582486225644-aeacf6aa0b1b", label: "Welcome home" },
  { id: "1494822493217-c9840aba840c", label: "Pacific Coast" },
]

const COLD_FALLBACKS = [
  "linear-gradient(135deg,#0a2a4f,#123b66)",
  "linear-gradient(135deg,#10243f,#1d4e84)",
  "linear-gradient(135deg,#0c3354,#2466a0)",
  "linear-gradient(135deg,#0a2540,#15457a)",
  "linear-gradient(135deg,#0e2c4d,#1b5288)",
  "linear-gradient(135deg,#09223c,#114a82)",
]

const CRYSTAL_COUNT = 26

const DeepFreezePaywall = () => {
  const { state, actions } = useAppState()
  const productData = state.productData

  const [showClose, setShowClose] = useState(false)

  useEffect(() => {
    actions.setIgnoreHeaderInset(true)
    actions.setHeaderInsetColor("#04101f")
    actions.setFooterInsetColor("#04101f")
    const t = setTimeout(() => setShowClose(true), 2800)
    return () => clearTimeout(t)
  }, [actions])

  const premium = productData?.premium
  const yearlyProduct = premium?.yearlyProduct
  const monthlyProduct = premium?.monthlyProduct
  const locale = yearlyProduct?.priceLocale

  const yearlyPrice = yearlyProduct
    ? formatCurrency(yearlyProduct.price, locale)
    : "$35.99"
  const perWeek = yearlyProduct
    ? formatCurrency(yearlyProduct.price / 52, locale)
    : "$0.69"
  const perMonthEquivalent = yearlyProduct
    ? formatCurrency(yearlyProduct.price / 12, locale)
    : "$2.99"
  const monthlyPrice = monthlyProduct
    ? formatCurrency(monthlyProduct.price, locale)
    : "$13.99"

  const numPhotos =
    productData?.numPhotos && productData.numPhotos > 0
      ? productData.numPhotos
      : 2174

  const savings = useMemo(() => {
    if (!yearlyProduct || !monthlyProduct) return 78
    const ratio = yearlyProduct.price / (monthlyProduct.price * 12)
    return Math.round((1 - ratio) * 100)
  }, [yearlyProduct, monthlyProduct])

  const topInset = state.initialData?.originalTopInset ?? 0
  const bottomInset = state.initialData?.originalBottomInset ?? 0

  const handleClose = () => native.postNativeEvent("close")

  const handlePurchase = () => {
    const product: Product | undefined = yearlyProduct
    if (product) native.subscribe(product)
  }

  return (
    <div className={styles.screen}>
      <FrostScene />

      <AnimatePresence>
        {showClose && (
          <motion.button
            className={styles.close}
            style={{ top: topInset + 14 }}
            onClick={handleClose}
            initial={{ opacity: 0, scale: 0.6 }}
            animate={{ opacity: 0.5, scale: 1 }}
            whileTap={{ scale: 0.85, opacity: 1 }}
            aria-label="Close"
          >
            ✕
          </motion.button>
        )}
      </AnimatePresence>

      <div
        className={styles.scroll}
        style={{ paddingTop: topInset + 16, paddingBottom: bottomInset + 14 }}
      >
        <StatusBar />

        <motion.div
          className={styles.heading}
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        >
          <h1 className={styles.title}>
            Your vault is
            <br />
            <span className={styles.titleAccent}>freezing over.</span>
          </h1>
          <p className={styles.subtitle}>
            Your access lapses when the timer hits zero. After that,{" "}
            <strong>{numPhotos.toLocaleString()} memories</strong> ice over and
            go dark — locked until you warm the vault back up.
          </p>
        </motion.div>

        <VaultGrid />

        <motion.div
          className={styles.plans}
          initial={{ opacity: 0, y: 22 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.6 }}
        >
          <div className={styles.yearCard}>
            <span className={styles.heatHalo} aria-hidden />
            <span className={styles.bestRibbon}>
              KEEP WARM · SAVE {savings}%
            </span>

            <div className={styles.yearHead}>
              <Flame />
              <div className={styles.yearHeadText}>
                <span className={styles.yearName}>Yearly — full thaw</span>
                <span className={styles.yearSub}>
                  Billed once · vault stays warm all year
                </span>
              </div>
            </div>

            <div className={styles.priceRow}>
              <span className={styles.priceMain}>{yearlyPrice}</span>
              <span className={styles.pricePer}>/year</span>
            </div>
            <div className={styles.priceSub}>
              just {perMonthEquivalent}/mo · {perWeek}/week
            </div>
          </div>

          <button className={styles.monthlyLine} onClick={handleClose}>
            Just stay frozen a little longer — {monthlyPrice}/mo, no discount
          </button>
        </motion.div>

        <motion.div
          className={styles.ctaWrap}
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.75, duration: 0.6 }}
        >
          <button className={styles.cta} onClick={handlePurchase}>
            <span className={styles.ctaShimmer} />
            <span className={styles.ctaLabel}>Keep my vault warm</span>
            <span className={styles.ctaArrow}>→</span>
          </button>
          <p className={styles.ctaNote}>
            Thaws instantly · Cancel anytime in one tap
          </p>
        </motion.div>

        <TrustRow />

        <div className={styles.legal}>
          <button onClick={() => native.postNativeEvent("terms")}>Terms</button>
          <span>·</span>
          <button onClick={() => native.postNativeEvent("privacy")}>
            Privacy
          </button>
          <span>·</span>
          <button onClick={() => native.postNativeEvent("already_purchased")}>
            Restore
          </button>
        </div>
      </div>
    </div>
  )
}

/** Cold backdrop: a frozen radial chill, creeping corner frost + drifting crystals. */
const FrostScene = () => {
  const crystals = useMemo(
    () =>
      Array.from({ length: CRYSTAL_COUNT }, (_, i) => {
        const r1 = Math.abs(Math.sin(i * 12.9898) * 43758.5453) % 1
        const r2 = Math.abs(Math.sin(i * 78.233) * 12543.123) % 1
        const r3 = Math.abs(Math.sin(i * 4.123) * 9973.77) % 1
        return {
          left: `${r1 * 100}%`,
          size: 2 + r3 * 4,
          delay: `${r2 * 9}s`,
          dur: `${7 + r1 * 8}s`,
          drift: `${(r3 - 0.5) * 60}px`,
        }
      }),
    [],
  )

  return (
    <div className={styles.scene} aria-hidden>
      <div className={styles.chill} />
      <div className={styles.frostTL} />
      <div className={styles.frostBR} />
      <div className={styles.vignette} />
      <div className={styles.crystals}>
        {crystals.map((c, i) => (
          <span
            key={i}
            className={styles.crystal}
            style={
              {
                left: c.left,
                width: c.size,
                height: c.size,
                animationDelay: c.delay,
                animationDuration: c.dur,
                "--drift": c.drift,
              } as React.CSSProperties
            }
          />
        ))}
      </div>
    </div>
  )
}

/** Live status bar: expiration countdown + a "% frozen" meter that climbs. */
const StatusBar = () => {
  const [secs, setSecs] = useState(9 * 60 + 58)
  const [frozen, setFrozen] = useState(71)

  useEffect(() => {
    const id = setInterval(() => {
      setSecs((s) => (s > 0 ? s - 1 : 0))
      setFrozen((f) => Math.min(99, +(f + 0.05).toFixed(2)))
    }, 1000)
    return () => clearInterval(id)
  }, [])

  const mm = String(Math.floor(secs / 60)).padStart(2, "0")
  const ss = String(secs % 60).padStart(2, "0")

  return (
    <motion.div
      className={styles.status}
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
    >
      <div className={styles.countdown}>
        <Snowflake small />
        <div className={styles.countText}>
          <span className={styles.countLabel}>Access freezes in</span>
          <span className={styles.countClock}>
            {mm}:{ss}
          </span>
        </div>
      </div>
      <div className={styles.meter}>
        <div className={styles.meterTop}>
          <span className={styles.meterLabel}>Vault temperature</span>
          <span className={styles.meterValue}>
            {Math.round(frozen)}% frozen
          </span>
        </div>
        <div className={styles.meterTrack}>
          <motion.div
            className={styles.meterFill}
            animate={{ width: `${frozen}%` }}
            transition={{ duration: 0.9, ease: "linear" }}
          />
        </div>
      </div>
    </motion.div>
  )
}

/** The hero: the user's memories inside a chilled glass vault, frosting over. */
const VaultGrid = () => {
  return (
    <motion.div
      className={styles.vault}
      initial={{ opacity: 0, scale: 0.94 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay: 0.3, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
    >
      <div className={styles.vaultGlass}>
        <div className={styles.grid}>
          {MEMORIES.map((m, i) => (
            <Tile key={m.id} memory={m} index={i} />
          ))}
        </div>
        <div className={styles.glassSheen} />
      </div>

      <div className={styles.vaultLockWrap}>
        <div className={styles.vaultLock}>
          <Snowflake />
        </div>
        <span className={styles.vaultLockLabel}>FREEZE LOCK ENGAGING</span>
      </div>
    </motion.div>
  )
}

const Tile = ({
  memory,
  index,
}: {
  memory: { id: string; label: string }
  index: number
}) => {
  const [failed, setFailed] = useState(false)
  // The frost on each tile thickens on a staggered delay so the grid visibly
  // ices over from the corners inward.
  const frostDelay = `${0.6 + index * 0.45}s`

  return (
    <motion.div
      className={styles.tile}
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.35 + index * 0.07, duration: 0.5 }}
    >
      {failed ? (
        <div
          className={styles.tileImg}
          style={{ background: COLD_FALLBACKS[index % COLD_FALLBACKS.length] }}
        />
      ) : (
        <img
          className={styles.tileImg}
          src={MEMORY_IMG.replace("IMGID", memory.id)}
          alt=""
          onError={() => setFailed(true)}
        />
      )}
      <div
        className={styles.tileFrost}
        style={{ animationDelay: frostDelay }}
      />
      <div
        className={styles.tileCracks}
        style={{ animationDelay: frostDelay }}
      />
      <span className={styles.tileLabel}>{memory.label}</span>
    </motion.div>
  )
}

const TrustRow = () => {
  return (
    <motion.div
      className={styles.trust}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 0.95, duration: 0.6 }}
    >
      <div className={styles.trustItem}>
        <span className={styles.stars5}>★★★★★</span>
        <span className={styles.trustLabel}>4.8 App Store</span>
      </div>
      <div className={styles.trustDivider} />
      <div className={styles.trustItem}>
        <span className={styles.trustBig}>10M+</span>
        <span className={styles.trustLabel}>vaults kept warm</span>
      </div>
      <div className={styles.trustDivider} />
      <div className={styles.trustItem}>
        <span className={styles.trustBig}>🔒</span>
        <span className={styles.trustLabel}>zero-knowledge</span>
      </div>
    </motion.div>
  )
}

const Snowflake = ({ small = false }: { small?: boolean }) => {
  const s = small ? 16 : 30
  return (
    <svg viewBox="0 0 24 24" width={s} height={s} fill="none" aria-hidden>
      <g
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M12 2v20M2 12h20M4.9 4.9l14.2 14.2M19.1 4.9 4.9 19.1" />
        <path d="M12 5.2 10 7m2-1.8L14 7M12 18.8 10 17m2 1.8L14 17M5.2 12 7 10m-1.8 2L7 14M18.8 12 17 10m1.8 2L17 14" />
      </g>
    </svg>
  )
}

const Flame = () => (
  <span className={styles.flame} aria-hidden>
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none">
      <path
        d="M12 2.5c.8 3.2-1.7 4.4-3.1 6.2C7.4 10.6 7 12.2 7 13.6 7 17.1 9.6 20 12.4 20c3 0 5.1-2.4 5.1-5.6 0-2.1-1-3.8-2.1-5.2-.4 1-1.2 1.6-2 1.6 1-2.2.3-5.6-1.4-8.3Z"
        fill="#fff"
      />
    </svg>
  </span>
)

export default DeepFreezePaywall
